import { useState, useEffect } from 'react';
import { getTimeRemaining } from '@cloudfruit-hunts/shared';

interface AnswerInputProps {
  onSubmit: (answer: string) => void;
  isSubmitting: boolean;
  disabled?: boolean;
  cooldownUntil?: string | null;
}

export function AnswerInput({ onSubmit, isSubmitting, disabled, cooldownUntil }: AnswerInputProps) {
  const [answer, setAnswer] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  useEffect(() => {
    if (!cooldownUntil) {
      setCooldownSeconds(0);
      return;
    }

    const updateCooldown = () => {
      const remaining = getTimeRemaining(cooldownUntil);
      if (remaining.isExpired) {
        setCooldownSeconds(0);
      } else {
        setCooldownSeconds(Math.ceil(remaining.total / 1000));
      }
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [cooldownUntil]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (answer.trim() && !isSubmitting && !disabled && cooldownSeconds === 0) {
      onSubmit(answer.trim());
      setAnswer('');
    }
  };

  const isDisabled = isSubmitting || disabled || cooldownSeconds > 0;

  return (
    <form onSubmit={handleSubmit} className="flex gap-3">
      <input
        type="text"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder="Enter your answer..."
        className="input flex-1"
        disabled={isDisabled}
      />
      <button
        type="submit"
        disabled={isDisabled || !answer.trim()}
        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
      >
        {isSubmitting ? (
          <span className="flex items-center gap-2">
            <Spinner />
            Checking...
          </span>
        ) : cooldownSeconds > 0 ? (
          `Wait ${cooldownSeconds}s`
        ) : (
          'Submit'
        )}
      </button>
    </form>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
